import React, {
  ComponentProps,
  ComponentPropsWithRef,
  ReactElement,
  Ref,
} from 'react';

import PropTypes from 'prop-types';
import { CSSObject } from 'styled-components';

import Button from '@adsk/alloy-react-button';
import {
  pickKeys,
  optionalPropType,
  useForwardedRef,
} from '@adsk/alloy-react-helpers';
import Overlay from '@adsk/alloy-react-overlay';
import { StyleProp, stylePropType } from '@adsk/alloy-react-theme';

import DropdownButtonControl from './Control';
import { menuCategoryShape, menuItemShape } from '../../helpers/propTypes';
import {
  MenuCategoryType,
  MenuItemType,
  SharedProps,
} from '../../helpers/types';
import useMenuButton from '../../hooks/useMenuButton';
import Menu from '../../Menu';

type DropdownButtonProps<TKey extends string | number> = Omit<
  ComponentProps<typeof Button>,
  'defaultValue' | 'value' | 'onChange' | 'type'
> &
  Pick<
    ComponentProps<typeof Overlay>,
    'container' | 'placement' | 'show' | 'overlayRef'
  > &
  SharedProps<TKey> & {
    horizontal?: boolean;
    options?: (MenuItemType<TKey> | MenuCategoryType<TKey>)[];
    renderContent?: (
      props: ComponentPropsWithRef<typeof Menu<TKey>>,
    ) => ReactElement;
    menuStyle?: StyleProp;
  };
/**
 * Dropdown Button component with Menu
 * A single control that opens a menu of related actions.
 */
const DropdownButtonWrapped = <TKey extends string | number>(
  {
    value,
    options = [],
    onChange,
    renderContent = (p) => <Menu {...p} />,
    defaultValue = [],
    show: showProp,
    placement: placementProp = Overlay.PLACEMENTS.BOTTOM_END,
    container = document.body,
    variant = Button.VARIANTS.SECONDARY,
    menuStyle = { minWidth: 200 },
    overlayRef,
    ...props
  }: DropdownButtonProps<TKey>,
  ref: Ref<HTMLButtonElement>,
) => {
  const targetRef = useForwardedRef(ref);

  const { getControlProps, getMenuProps } = useMenuButton({
    defaultValue,
    value,
    onChange,
  });

  return (
    <Overlay<HTMLButtonElement>
      show={showProp}
      target={targetRef}
      overlayRef={overlayRef}
      placement={placementProp}
      container={container}
      renderTarget={(buttonProps) => (
        <DropdownButtonControl
          variant={variant}
          {...getControlProps({ show: buttonProps.show })}
          {...buttonProps}
          {...props}
        />
      )}
      renderOverlay={({ overlayProps, placement, handleHide }) =>
        renderContent({
          'data-testid': 'Dropdown__Menu',
          ref: overlayProps.ref,
          placement,
          options,
          defaultValue,
          onClose: handleHide,
          style: {
            ...(overlayProps.style as CSSObject),
            ...{ width: targetRef?.current?.offsetWidth },
            ...(menuStyle as CSSObject),
          },
          ...getMenuProps(),
        })
      }
    />
  );
};

const DropdownButton = React.forwardRef(DropdownButtonWrapped);

DropdownButton.displayName = 'DropdownButton';

DropdownButton.propTypes = {
  ...Button.propTypes,
  ...pickKeys<Record<string, unknown>>(
    Overlay.propTypes as Record<string, unknown>,
    ['container', 'placement', 'show', 'overlayRef'],
  ),
  /** Options for the action */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  options: PropTypes.arrayOf<any>(
    PropTypes.oneOfType([menuItemShape, menuCategoryShape]),
  ),
  /** Pre selected keys */
  defaultValue: optionalPropType(
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    ),
  ),
  /** Value for controlled usage */
  value: optionalPropType(
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    ),
  ),
  /** Handle for item click event */
  onChange: PropTypes.func,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** DropdownButton's content */
  children: PropTypes.any,
  /** Disable button functionality */
  disabled: PropTypes.bool,
  /** handle onClick event for button */
  onClick: PropTypes.func,
  /** render custom menu content, called with args ({ ref, placement, options, defaultValue, value, onChange, onClose, style }) */
  renderContent: PropTypes.func,
  /** Styles applied to the overlay element */
  menuStyle: stylePropType,
};

export default Object.assign(
  DropdownButton as <TKey extends string | number>(
    p: DropdownButtonProps<TKey> & { ref?: Ref<HTMLDivElement> },
  ) => ReactElement,
  {
    VARIANTS: Button.VARIANTS,
    SIZES: Button.SIZES,
    PLACEMENTS: Overlay.PLACEMENTS,
  },
);
